import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import stripePromise from "../config/stripeConfig";
import { createSubscription } from "../services/paymentService";
import { getOrganizationDetails } from "../services/organizationService";
import CountrySelect from "./CountrySelect";

const BASE_PRICE = 9; // $9 for up to 3 rooms
const BASE_ROOMS = 3;

// Stripe CardElement configuration - hide postal code since we collect full billing address separately
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
  },
  hidePostalCode: true, // We collect postal code in the billing address form above
};

function SubscriptionSetupForm({ organizationId, onSuccess }) {
  const { user } = useSelector((state) => state.user);
  
  const [loading, setLoading] = useState(false);
  const [billingEmail, setBillingEmail] = useState(() => {
    // Initialize with user's email if available
    return user?.email || "";
  });
  const [error, setError] = useState("");
  const [maxRooms, setMaxRooms] = useState(BASE_ROOMS); // Default to 3 rooms
  const [billingAddress, setBillingAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: ''
  });
  const [sameAsCompanyAddress, setSameAsCompanyAddress] = useState(true);
  const [organization, setOrganization] = useState(null);
  const hasSetDefaultEmail = useRef(false);

  const stripe = useStripe();
  const elements = useElements();
  const { enqueueSnackbar } = useSnackbar();

  // Load organization data
  useEffect(() => {
    const loadOrganizationData = async () => {
      try {
        const orgData = await getOrganizationDetails();
        setOrganization(orgData);
        
        // If organization has company address and sameAsCompanyAddress is true, copy it
        if (orgData?.companyAddress && sameAsCompanyAddress) {
          setBillingAddress(orgData.companyAddress);
        }
      } catch (error) {
        console.error('Error loading organization data:', error);
      }
    };

    loadOrganizationData();
  }, []);

  // Set default billing email to user's email if it wasn't set during initialization
  useEffect(() => {
    if (user && user.email && !billingEmail && !hasSetDefaultEmail.current) {
      setBillingEmail(user.email);
      hasSetDefaultEmail.current = true;
    }
  }, [user]); // Only depend on user, not billingEmail to avoid infinite loop

  // Handle "same as company address" checkbox
  useEffect(() => {
    if (sameAsCompanyAddress && organization?.companyAddress) {
      setBillingAddress(organization.companyAddress);
    } else if (!sameAsCompanyAddress) {
      setBillingAddress({
        line1: '',
        line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: ''
      });
    }
  }, [sameAsCompanyAddress, organization]);

  const handleBillingAddressChange = (e) => {
    const { name, value } = e.target;
    setBillingAddress(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    // For onboarding, always use base plan (3 rooms)
    const roomCount = BASE_ROOMS;

    setLoading(true);
    setError("");

    try {
      // Create payment method
      const cardElement = elements.getElement(CardElement);
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          email: billingEmail,
          address: {
            line1: billingAddress.line1,
            line2: billingAddress.line2,
            city: billingAddress.city,
            state: billingAddress.state,
            postal_code: billingAddress.postal_code,
            country: billingAddress.country,
          },
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      // Create subscription
      const response = await createSubscription({
        organizationId,
        paymentMethodId: paymentMethod.id,
        billingEmail,
        maxRooms: roomCount,
        billingAddress: billingAddress,
      });

      // Handle payment confirmation if needed
      if (response.clientSecret) {
        const { error: confirmError } = await stripe.confirmCardPayment(response.clientSecret);
        
        if (confirmError) {
          setError(confirmError.message);
          setLoading(false);
          return;
        }
      }

      enqueueSnackbar("Subscription created successfully!", { variant: "success" });
      onSuccess(response.subscription);

    } catch (err) {
      console.error("Subscription creation error:", err);
      const errorMessage = err.response?.data?.error || err.message || "Failed to create subscription";
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label="Billing Email"
            type="email"
            fullWidth
            required
            value={billingEmail}
            onChange={(e) => setBillingEmail(e.target.value)}
            sx={{ mb: 3 }}
            disabled={loading}
            helperText="Billing notifications will be sent to this email address"
          />

          {/* Billing Address Section */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Billing Address
          </Typography>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={sameAsCompanyAddress}
                onChange={(e) => setSameAsCompanyAddress(e.target.checked)}
                disabled={loading}
              />
            }
            label="Same as company address"
            sx={{ mb: 2 }}
          />

          {!sameAsCompanyAddress && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  name="line1"
                  value={billingAddress.line1}
                  onChange={handleBillingAddressChange}
                  required
                  placeholder="Street address"
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2 (Optional)"
                  name="line2"
                  value={billingAddress.line2}
                  onChange={handleBillingAddressChange}
                  placeholder="Apartment, suite, etc."
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={billingAddress.city}
                  onChange={handleBillingAddressChange}
                  required
                  placeholder="City"
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="State/Province"
                  name="state"
                  value={billingAddress.state}
                  onChange={handleBillingAddressChange}
                  placeholder="State"
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  name="postal_code"
                  value={billingAddress.postal_code}
                  onChange={handleBillingAddressChange}
                  placeholder="ZIP/Postal code"
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <CountrySelect
                  value={billingAddress.country}
                  onChange={(value) => setBillingAddress(prev => ({ ...prev, country: value }))}
                  required
                  disabled={loading}
                />
              </Grid>
            </Grid>
          )}


          <Divider sx={{ mb: 3 }} />

          {/* Payment Method Section */}
          <Typography variant="h6" gutterBottom>
            Payment Method
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your card details below. We'll use the billing address you entered above for verification.
          </Typography>
          
          <Box
            sx={{
              border: '1px solid #ccc',
              borderRadius: 1,
              p: 2,
              mb: 3,
            }}
          >
            <CardElement options={cardElementOptions} />
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Test Cards:
            <br />
            • Success: 4242 4242 4242 4242
            <br />
            • Decline: 4000 0000 0000 0002
            <br />
            Use any future date and any 3-digit CVC.
          </Typography>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={!stripe || loading}
            sx={{ mt: 2 }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Creating Subscription...
              </>
            ) : (
              "Create Subscription"
            )}
          </Button>
        </Box>
    </Box>
  );
}

export default function SubscriptionSetup({ organizationId, onSuccess }) {
  return (
    <Elements stripe={stripePromise}>
      <SubscriptionSetupForm organizationId={organizationId} onSuccess={onSuccess} />
    </Elements>
  );
} 